import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";

const INTERVAL = 5000;

const HeroSlider = ({ onReady }) => {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);

  // evita disparar onReady varias veces
  const readyFiredRef = useRef(false);

  const fallback = useMemo(
    () => ["/fotos/IMG_1273.jpg", "/fotos/IMG_1272.jpg"],
    []
  );

  // 1) Traer carrusel desde Firestore (con fallback)
  useEffect(() => {
    const q = query(
      collection(db, "carrusel_home"),
      where("activo", "==", true),
      orderBy("orden", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // normaliza nombres posibles para URL
        const normalized = raw
          .map((x) => ({
            ...x,
            url:
              x.url ||
              x.imageUrl ||
              x.fotoURL ||
              x.imagen ||
              x.imagenUrl ||
              x.src,
          }))
          .filter((x) => !!x.url);

        const list = normalized.length
          ? normalized
          : fallback.map((url, i) => ({ id: `fb-${i}`, url }));

        readyFiredRef.current = false; // reinicia si cambia data
        setSlides(list);
        setIndex(0);
      },
      () => {
        readyFiredRef.current = false;
        setSlides(fallback.map((url, i) => ({ id: `fb-${i}`, url })));
        setIndex(0);
      }
    );

    return () => unsub();
  }, [fallback]);

  // 2) Precargar SIEMPRE el slide visible y recién ahí avisar al Home
  useEffect(() => {
    if (!slides.length) return;

    const currentUrl = slides[index]?.url;
    if (!currentUrl) return;

    let cancelled = false;

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (!readyFiredRef.current) {
        readyFiredRef.current = true;
        onReady?.();
      }
    };
    img.onerror = () => {
      // si falla la imagen, igual liberamos el splash para no quedar pegados
      if (cancelled) return;
      if (!readyFiredRef.current) {
        readyFiredRef.current = true;
        onReady?.();
      }
    };

    img.src = currentUrl;

    return () => {
      cancelled = true;
    };
  }, [slides, index, onReady]);

  // 3) Autoplay
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, INTERVAL);
    return () => clearInterval(id);
  }, [slides]);

  const handleNext = () => setIndex((prev) => (prev + 1) % slides.length);
  const handlePrev = () =>
    setIndex((prev) => (prev - 1 + slides.length) % slides.length);

  if (!slides.length) return null;

  return (
    <div className="relative max-w-md mx-auto">
      <div className="relative rounded-3xl overflow-hidden shadow-xl border border-white/60 bg-white/80 backdrop-blur">
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.url}
            alt={s.titulo || `EKINE slide ${i}`}
            className={`
              absolute inset-0 w-full h-full object-cover
              transition-opacity duration-700
              ${i === index ? "opacity-100" : "opacity-0"}
            `}
            loading={i === index ? "eager" : "lazy"}
            draggable="false"
          />
        ))}

        {/* Mantiene ratio del contenedor */}
        <div className="w-full h-0 pb-[125%]" />
      </div>

      {slides.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={handlePrev}
            className="p-2 rounded-full bg-white shadow-md border hover:bg-gray-50 transition"
            aria-label="Anterior"
          >
            ‹
          </button>
          <button
            onClick={handleNext}
            className="p-2 rounded-full bg-white shadow-md border hover:bg-gray-50 transition"
            aria-label="Siguiente"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default HeroSlider;
